import React from "react";
import {
  Auth,
  User,
  UserCredential,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";

import { AuthContext, FirebaseContext } from "@contexts";
import { AuthUser } from "./firestoreHooks";

export type UseAuthData = {
  auth: Auth;
  user: AuthUser | null;
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<User | void>;
  logout: () => Promise<void>;
  goToLogin: () => void;
};

export function useAuth(): UseAuthData {
  const { auth } = React.useContext(FirebaseContext);
  const { authUser, user, setUser, clearUser } = React.useContext(AuthContext);
  const navigate = useNavigate();

  const outputAuthUser = React.useMemo(() => {
    if (user && authUser) {
      return {
        firebaseUser: user,
        ...authUser,
      } as AuthUser;
    }
    return null;
  }, [user, authUser]);

  const login = React.useCallback(
    async (email: string, password: string): Promise<User | void> => {
      try {
        const { user }: UserCredential = await signInWithEmailAndPassword(
          auth,
          email,
          password,
        );
        setUser(user);
        return user;
      } catch (ex) {
        // TODO: Fix catch https://github.com/brown-ccv/collider-parts-db/issues/7
        console.error(`An error occured when logging in user ${email}`);
        console.error({ ex });
      }
    },
    [auth, setUser],
  );

  const logout = React.useCallback(async () => {
    try {
      await signOut(auth);
      clearUser();
      navigate("/login");
    } catch (ex) {
      // TODO: Fix catch https://github.com/brown-ccv/collider-parts-db/issues/7
      console.error("An error occured when logging out current user");
      console.error({ ex });
    }
  }, [auth, clearUser]);

  const goToLogin = React.useCallback(() => navigate("/login"), [navigate]);

  return {
    auth,
    user: outputAuthUser,
    isLoggedIn: !!user,
    login,
    logout,
    goToLogin,
  };
}
